import React, { useState, useEffect, useMemo, useRef, useContext, useCallback } from 'react';
import axios from 'axios';
import { UserContext, WorkspaceContext, OrgContext } from '../../objects/Context';
import { ReactComponent as DownloadIcon } from '../../assets/icons/download-icon.svg';
import { ReactComponent as RefreshIcon } from '../../assets/icons/refresh-icon.svg';
import { downloadPnLExcel, generatePnLExcelBlob } from '../../services/excelModel/excelPLServiceBrowser';
import './ProfitAndLoss.css';

// ─── Date helpers (UTC-based to match backend bucketing) ─────────────────────
const pad = (n) => String(n).padStart(2, '0');
// Normalize via Date.UTC so month/day overflow (e.g. month=-1 or day=32) wraps
// correctly into the prior/next year.
const utcIso = (y, m, d) => new Date(Date.UTC(y, m, d)).toISOString().slice(0, 10);

const startOfMonthIso   = (d) => utcIso(d.getUTCFullYear(), d.getUTCMonth(), 1);
const startOfQuarterIso = (d) => utcIso(d.getUTCFullYear(), Math.floor(d.getUTCMonth() / 3) * 3, 1);
const startOfYearIso    = (d) => `${d.getUTCFullYear()}-01-01`;

const todayUTC = () => {
  const n = new Date();
  return new Date(Date.UTC(n.getFullYear(), n.getMonth(), n.getDate()));
};

const addMonthsIso = (iso, n) => {
  const d = new Date(iso + 'T00:00:00Z');
  return utcIso(d.getUTCFullYear(), d.getUTCMonth() + n, d.getUTCDate());
};

// Format an inclusive range label given an exclusive [start, end)
const fmtRangeLabel = ({ startDate, endDate }) => {
  if (!startDate || !endDate) return '';
  const s = new Date(startDate + 'T00:00:00Z');
  const e = new Date(endDate + 'T00:00:00Z');
  const eMinus1 = new Date(e.getTime() - 24 * 60 * 60 * 1000);
  const opts = { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' };
  return `${s.toLocaleDateString('en-US', opts)} – ${eMinus1.toLocaleDateString('en-US', opts)}`;
};

const fmtColumnRange = ({ startDate, endDate }) => {
  if (!startDate || !endDate) return '';
  const s = new Date(startDate + 'T00:00:00Z');
  const e = new Date(endDate + 'T00:00:00Z');
  const eMinus1 = new Date(e.getTime() - 24 * 60 * 60 * 1000);
  const opts = { month: 'short', year: 'numeric', timeZone: 'UTC' };
  const sLabel = s.toLocaleDateString('en-US', opts);
  const eLabel = eMinus1.toLocaleDateString('en-US', opts);
  return sLabel === eLabel ? sLabel : `${sLabel} – ${eLabel}`;
};

const fmtCurrency = (v) => {
  if (v == null || isNaN(v)) return '—';
  const n = Math.round(v);
  if (n === 0) return '0';
  const abs = Math.abs(n).toLocaleString('en-US');
  return n < 0 ? `(${abs})` : abs;
};

// ─── View modes ──────────────────────────────────────────────────────────────
const VIEW_MODES = [
  { key: 'total',     label: 'Total',     granularity: 'period' },
  { key: 'monthly',   label: 'Monthly',   granularity: 'monthly' },
  { key: 'quarterly', label: 'Quarterly', granularity: 'quarterly' },
];

// Total-mode range presets (single period)
const TOTAL_PRESETS = [
  { key: 'this_month',   label: 'This Month' },
  { key: 'last_month',   label: 'Last Month' },
  { key: 'this_quarter', label: 'This Quarter' },
  { key: 'last_quarter', label: 'Last Quarter' },
  { key: 'ytd',          label: 'Year to Date' },
  { key: 'last_year',    label: 'Last Year' },
];

// Trend-mode range presets (multi-period)
const TREND_PRESETS = [
  { key: 'last_3m',   label: 'Last 3 Months' },
  { key: 'last_6m',   label: 'Last 6 Months' },
  { key: 'last_12m',  label: 'Last 12 Months' },
  { key: 'ytd',       label: 'Year to Date' },
  { key: 'last_year', label: 'Last Year' },
];

const COMPARE_PRESETS = [
  { key: 'none',            label: 'No Comparison' },
  { key: 'previous_period', label: 'Previous Period' },
  { key: 'previous_year',   label: 'Previous Year' },
];

const TOTAL_PRESET_KEYS = new Set(TOTAL_PRESETS.map(p => p.key));
const TREND_PRESET_KEYS = new Set(TREND_PRESETS.map(p => p.key));

// ─── Range computation ──────────────────────────────────────────────────────
function computeRangeFromPreset(preset, viewMode) {
  const today = todayUTC();
  const todayPlus1 = utcIso(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() + 1);

  switch (preset) {
    case 'this_month': {
      const s = startOfMonthIso(today);
      return { startDate: s, endDate: addMonthsIso(s, 1) };
    }
    case 'last_month': {
      const s = addMonthsIso(startOfMonthIso(today), -1);
      return { startDate: s, endDate: addMonthsIso(s, 1) };
    }
    case 'this_quarter': {
      const s = startOfQuarterIso(today);
      return { startDate: s, endDate: addMonthsIso(s, 3) };
    }
    case 'last_quarter': {
      const s = addMonthsIso(startOfQuarterIso(today), -3);
      return { startDate: s, endDate: addMonthsIso(s, 3) };
    }
    case 'ytd': {
      // In Total mode: Jan 1 → today (so YTD reflects activity through today).
      // In Trend mode: Jan 1 → start of NEXT month (so the current month appears as a column).
      const s = startOfYearIso(today);
      const end = viewMode === 'total' ? todayPlus1 : addMonthsIso(startOfMonthIso(today), 1);
      return { startDate: s, endDate: end };
    }
    case 'last_year': {
      const yr = today.getUTCFullYear() - 1;
      return { startDate: `${yr}-01-01`, endDate: `${today.getUTCFullYear()}-01-01` };
    }
    case 'last_3m':
    case 'last_6m':
    case 'last_12m': {
      // Trailing N months ending with the current (in-progress) month.
      const monthsBack = preset === 'last_3m' ? 3 : preset === 'last_6m' ? 6 : 12;
      const endIso = addMonthsIso(startOfMonthIso(today), 1);
      const startIso = addMonthsIso(endIso, -monthsBack);
      return { startDate: startIso, endDate: endIso };
    }
    default:
      return null;
  }
}

// Shift a UTC date by `years`, clamping the day-of-month so Feb 29 of a leap
// year doesn't silently roll into March 1 of the target year.
function shiftYearsClampDay(date, years) {
  const targetY = date.getUTCFullYear() + years;
  const targetM = date.getUTCMonth();
  const day = date.getUTCDate();
  const lastDayOfTargetMonth = new Date(Date.UTC(targetY, targetM + 1, 0)).getUTCDate();
  return utcIso(targetY, targetM, Math.min(day, lastDayOfTargetMonth));
}

// If [start, end) is calendar-month-aligned (both fall on the 1st), return the
// month-count between them; otherwise null. Used to detect ranges like a
// calendar month, quarter, or year so "previous period" can shift by full
// months instead of by raw day-count.
function calendarMonthSpan(start, end) {
  if (start.getUTCDate() !== 1 || end.getUTCDate() !== 1) return null;
  const months = (end.getUTCFullYear() - start.getUTCFullYear()) * 12
    + (end.getUTCMonth() - start.getUTCMonth());
  return months > 0 ? months : null;
}

function computeComparisonRange(period, mode) {
  if (!period || mode === 'none' || !mode) return null;
  const start = new Date(period.startDate + 'T00:00:00Z');
  const end = new Date(period.endDate + 'T00:00:00Z');
  if (mode === 'previous_year') {
    return {
      startDate: shiftYearsClampDay(start, -1),
      endDate: shiftYearsClampDay(end, -1),
    };
  }
  if (mode === 'previous_period') {
    // Calendar-aligned ranges (e.g. a month, quarter, year) shift by full
    // months so the prior calendar month/quarter/year is returned as-is.
    const monthSpan = calendarMonthSpan(start, end);
    if (monthSpan != null) {
      return {
        startDate: addMonthsIso(period.startDate, -monthSpan),
        endDate: period.startDate,
      };
    }
    // Fallback: arbitrary ranges shift by exact day-count.
    const days = Math.round((end - start) / (24 * 60 * 60 * 1000));
    const cs = new Date(start.getTime() - days * 24 * 60 * 60 * 1000);
    return { startDate: cs.toISOString().slice(0, 10), endDate: period.startDate };
  }
  return null;
}

// ─── Periods (local mirror of backend bucketing) ────────────────────────────
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function buildPeriodsLocal(range, granularity) {
  if (granularity === 'period') {
    return [{ key: range.startDate, label: '', startDate: range.startDate, endDate: range.endDate }];
  }
  const periods = [];
  const startD = new Date(range.startDate + 'T00:00:00Z');
  const endD = new Date(range.endDate + 'T00:00:00Z');
  let cur;
  if (granularity === 'monthly') {
    cur = new Date(Date.UTC(startD.getUTCFullYear(), startD.getUTCMonth(), 1));
  } else {
    cur = new Date(Date.UTC(startD.getUTCFullYear(), Math.floor(startD.getUTCMonth() / 3) * 3, 1));
  }
  while (cur < endD) {
    const y = cur.getUTCFullYear();
    const m = cur.getUTCMonth();
    let periodEnd, label;
    if (granularity === 'monthly') {
      periodEnd = new Date(Date.UTC(y, m + 1, 1));
      label = `${MONTH_NAMES[m]} ${y}`;
    } else {
      periodEnd = new Date(Date.UTC(y, m + 3, 1));
      label = `Q${Math.floor(m / 3) + 1} ${y}`;
    }
    periods.push({
      key: `${y}-${pad(m + 1)}-01`,
      label,
      startDate: cur.toISOString().slice(0, 10),
      endDate: periodEnd.toISOString().slice(0, 10),
    });
    cur = periodEnd;
  }
  return periods.length ? periods : [{ key: range.startDate, label: '', startDate: range.startDate, endDate: range.endDate }];
}

// ─── Variance coloring ───────────────────────────────────────────────────────
const varianceClass = (variance, favorable) => {
  if (variance == null || Math.abs(variance) < 0.5) return '';
  const isPositive = variance > 0;
  const good = (favorable === 'positive') ? isPositive : !isPositive;
  return good ? 'pl-pos' : 'pl-neg';
};

// ─── Account row (recursive, collapsable) ────────────────────────────────────
// When a parent has children and is expanded, values move out of the parent row
// and appear in a bold "Total [name]" subtotal row beneath the children. When
// collapsed, the parent row shows the rolled-up values directly.
const AccountRow = ({ node, depth, periods, showTotal, showCompare, favorable, expanded, onToggle, idPath }) => {
  const hasChildren = node.children && node.children.length > 0;
  const isOpen = expanded[idPath] !== false; // default expanded
  const showValuesOnParent = !hasChildren || !isOpen;
  return (
    <>
      <tr className={`pl-row pl-account ${hasChildren ? 'pl-has-children' : ''} ${depth === 0 ? 'pl-account-top' : ''} ${hasChildren && isOpen ? 'pl-parent-open' : ''}`}>
        <td className="pl-name-cell pl-sticky-col" style={{ paddingLeft: 32 + depth * 16 }}>
          <div className="pl-name-content">
            {hasChildren ? (
              <button className="pl-chevron" onClick={() => onToggle(idPath)} aria-label={isOpen ? 'Collapse' : 'Expand'}>
                <svg className={`pl-chevron-icon ${isOpen ? 'open' : 'closed'}`} viewBox="0 0 10 10" aria-hidden="true">
                  <path d="M3 1.5 L7 5 L3 8.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            ) : (
              <span className="pl-chevron-spacer" />
            )}
            <span className="pl-account-name">{node.name}</span>
          </div>
        </td>
        {periods.map((p, i) => (
          <td key={p.key} className="pl-amount-cell">{showValuesOnParent ? fmtCurrency(node.values[i]) : ''}</td>
        ))}
        {showTotal && <td className="pl-amount-cell pl-total-col">{showValuesOnParent ? fmtCurrency(node.total) : ''}</td>}
        {showCompare && <td className="pl-amount-cell pl-cmp">{showValuesOnParent ? fmtCurrency(node.total_comparison) : ''}</td>}
        {showCompare && (
          <td className={`pl-amount-cell pl-variance ${showValuesOnParent ? varianceClass(node.total_variance, favorable) : ''}`}>
            {showValuesOnParent ? fmtCurrency(node.total_variance) : ''}
          </td>
        )}
      </tr>
      {hasChildren && isOpen && (
        <>
          {node.children.map((child) => (
            <AccountRow
              key={child.id}
              node={child}
              depth={depth + 1}
              periods={periods}
              showTotal={showTotal}
              showCompare={showCompare}
              favorable={favorable}
              expanded={expanded}
              onToggle={onToggle}
              idPath={`${idPath}/${child.id}`}
            />
          ))}
          <tr className="pl-row pl-account-subtotal">
            <td className="pl-name-cell pl-sticky-col" style={{ paddingLeft: 32 + depth * 16 }}>
              <div className="pl-name-content">
                <span className="pl-chevron-spacer" />
                <span className="pl-account-subtotal-label">Total {node.name}</span>
              </div>
            </td>
            {periods.map((p, i) => (
              <td key={p.key} className="pl-amount-cell">{fmtCurrency(node.values[i])}</td>
            ))}
            {showTotal && <td className="pl-amount-cell pl-total-col">{fmtCurrency(node.total)}</td>}
            {showCompare && <td className="pl-amount-cell pl-cmp">{fmtCurrency(node.total_comparison)}</td>}
            {showCompare && (
              <td className={`pl-amount-cell pl-variance ${varianceClass(node.total_variance, favorable)}`}>
                {fmtCurrency(node.total_variance)}
              </td>
            )}
          </tr>
        </>
      )}
    </>
  );
};

// ─── Skeleton ────────────────────────────────────────────────────────────────
const SkeletonRow = ({ idx, columnCount }) => (
  <tr className="pl-row pl-skeleton-row">
    <td className="pl-name-cell pl-sticky-col"><div className="pl-skel-bar" style={{ width: `${50 + (idx % 4) * 8}%` }} /></td>
    {Array.from({ length: columnCount - 1 }).map((_, c) => (
      <td key={c} className="pl-amount-cell"><div className="pl-skel-bar" style={{ width: '60%', marginLeft: 'auto' }} /></td>
    ))}
  </tr>
);

// ─── Main page ───────────────────────────────────────────────────────────────
function ProfitAndLoss() {
  const { user } = useContext(UserContext);
  const { workspace } = useContext(WorkspaceContext);
  const { org } = useContext(OrgContext);

  const [viewMode, setViewMode] = useState('total');
  const granularity = useMemo(() => VIEW_MODES.find(v => v.key === viewMode).granularity, [viewMode]);
  const isTrend = viewMode !== 'total';

  // Range state
  const [rangePreset, setRangePreset] = useState('ytd');
  const [range, setRange] = useState(computeRangeFromPreset('ytd', 'total'));
  const [customStart, setCustomStart] = useState(range.startDate);
  const [customEnd, setCustomEnd]     = useState(range.endDate);

  // Compare state (only used in 'total' mode)
  const [comparePreset, setComparePreset] = useState('none');
  const [customCmpStart, setCustomCmpStart] = useState('');
  const [customCmpEnd, setCustomCmpEnd]     = useState('');

  // Atomic view-mode change: also sync preset + range in the same handler so
  // React batches into a single render (avoids a double-fetch with stale args).
  const handleSetViewMode = (newMode) => {
    if (newMode === viewMode) return;
    const newIsTrend = newMode !== 'total';
    const validNow = newIsTrend ? TREND_PRESET_KEYS.has(rangePreset) : TOTAL_PRESET_KEYS.has(rangePreset);
    const nextPreset = (validNow || rangePreset === 'custom') ? rangePreset : (newIsTrend ? 'last_12m' : 'ytd');
    const nextRange = nextPreset === 'custom' ? range : computeRangeFromPreset(nextPreset, newMode);

    setViewMode(newMode);
    if (nextPreset !== rangePreset) setRangePreset(nextPreset);
    if (nextRange) {
      setRange(nextRange);
      setCustomStart(nextRange.startDate);
      setCustomEnd(nextRange.endDate);
    }
  };

  // Atomic preset change: also sync range and custom-date inputs.
  const handleSetRangePreset = (preset) => {
    setRangePreset(preset);
    if (preset !== 'custom') {
      const r = computeRangeFromPreset(preset, viewMode);
      if (r) {
        setRange(r);
        setCustomStart(r.startDate);
        setCustomEnd(r.endDate);
      }
    }
    setRangeOpen(false);
  };

  // Resolve effective comparison range
  const comparisonRange = useMemo(() => {
    if (isTrend) return null;
    if (comparePreset === 'none') return null;
    if (comparePreset === 'custom') {
      if (!customCmpStart || !customCmpEnd || customCmpStart >= customCmpEnd) return null;
      return { startDate: customCmpStart, endDate: customCmpEnd };
    }
    return computeComparisonRange(range, comparePreset);
  }, [comparePreset, range, customCmpStart, customCmpEnd, isTrend]);

  // UI state
  const [rangeOpen, setRangeOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const rangeRef = useRef(null);
  const compareRef = useRef(null);
  const downloadRef = useRef(null);
  const tableWrapperRef = useRef(null);

  // Data state
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [isHorizScrolled, setIsHorizScrolled] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    const handleClick = (e) => {
      if (rangeRef.current && !rangeRef.current.contains(e.target)) setRangeOpen(false);
      if (compareRef.current && !compareRef.current.contains(e.target)) setCompareOpen(false);
      if (downloadRef.current && !downloadRef.current.contains(e.target)) setDownloadOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Toggle a subtle right-edge shadow on the sticky account column when the
  // table is horizontally scrolled (signals scrollable content beneath).
  useEffect(() => {
    const el = tableWrapperRef.current;
    if (!el) return;
    const onScroll = () => setIsHorizScrolled(el.scrollLeft > 0);
    onScroll();
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [data, loading]);

  // Fetch P&L. Accepts an AbortSignal so stale requests can be cancelled when
  // filters change rapidly (prevents an older response from clobbering newer data).
  const fetchPnL = useCallback(async (signal) => {
    if (!user?.id || !workspace?.id) return;
    if (!range?.startDate || !range?.endDate) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('granularity', granularity);
      params.set('startDate', range.startDate);
      params.set('endDate', range.endDate);
      if (!isTrend && comparisonRange) {
        params.set('compareStartDate', comparisonRange.startDate);
        params.set('compareEndDate', comparisonRange.endDate);
      }
      const url = `${process.env.REACT_APP_API_BASE_URL}/api/queries/profit-and-loss/${encodeURIComponent(user.id)}/${encodeURIComponent(workspace.id)}?${params.toString()}`;
      const res = await axios.get(url, { withCredentials: true, signal });
      if (res.data?.success) {
        setData(res.data);
      } else {
        setData(null);
        setError('Failed to load profit and loss.');
      }
    } catch (err) {
      if (axios.isCancel?.(err) || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
        return; // Superseded by a newer request — leave existing state alone.
      }
      console.error('Failed to fetch P&L:', err);
      setError(err.response?.data?.error || 'Failed to load profit and loss.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [user?.id, workspace?.id, range, granularity, comparisonRange, isTrend]);

  useEffect(() => {
    const controller = new AbortController();
    fetchPnL(controller.signal);
    return () => controller.abort();
  }, [fetchPnL]);

  const isCustomRangeValid = !!(customStart && customEnd && customStart < customEnd);
  const isCustomCompareValid = !!(customCmpStart && customCmpEnd && customCmpStart < customCmpEnd);

  const applyCustomRange = () => {
    if (!isCustomRangeValid) return;
    setRange({ startDate: customStart, endDate: customEnd });
    setRangePreset('custom');
    setRangeOpen(false);
  };

  const applyCustomCompare = () => {
    if (!isCustomCompareValid) return;
    setComparePreset('custom');
    setCompareOpen(false);
  };

  const toggleNode = (idPath) => {
    setExpanded(prev => ({ ...prev, [idPath]: prev[idPath] === false }));
  };

  // Reset all filters & UI state back to defaults.
  const handleReset = () => {
    const defaultRange = computeRangeFromPreset('ytd', 'total');
    setViewMode('total');
    setRangePreset('ytd');
    setRange(defaultRange);
    setCustomStart(defaultRange.startDate);
    setCustomEnd(defaultRange.endDate);
    setComparePreset('none');
    setCustomCmpStart('');
    setCustomCmpEnd('');
    setExpanded({});
    setRangeOpen(false);
    setCompareOpen(false);
  };

  // ── Download (Excel / PDF) ─────────────────────────────────
  const buildExportFilename = () => {
    const wsName = (workspace?.name || workspace?.title || 'profit_and_loss').replace(/\s+/g, '_');
    return `${wsName}_profit_and_loss_${new Date().toISOString().slice(0, 10)}`;
  };

  const buildExportMetadata = () => {
    const clientPortal = org?.clientPortal || {};
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
    const logoDownloadUrl = (clientPortal.logoUrl && org?.id && apiBaseUrl)
      ? `${apiBaseUrl}/api/orgs/logo-file/${org.id}`
      : null;
    return {
      workspaceName: workspace?.name || workspace?.title || '',
      statementName: 'Profit & Loss',
      branding: { ...clientPortal, logoDownloadUrl },
    };
  };

  const handleDownloadExcel = async () => {
    if (isExporting || loading || !data) return;
    setDownloadOpen(false);
    try {
      setIsExporting(true);
      await downloadPnLExcel(data, buildExportFilename(), buildExportMetadata());
    } catch (err) {
      console.error('P&L Excel export failed:', err);
      alert('Failed to export P&L. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (isExporting || loading || !data) return;
    setDownloadOpen(false);
    try {
      setIsExporting(true);
      const filename = buildExportFilename();
      const workbookBlob = await generatePnLExcelBlob(data, buildExportMetadata());

      const formData = new FormData();
      formData.append('file', workbookBlob, `${filename}.xlsx`);

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/forecasts/export-pdf`,
        formData,
        { withCredentials: true, responseType: 'blob' }
      );

      // Guard against the server returning a non-PDF (e.g. an HTML/JSON error
      // body) — `responseType: 'blob'` would otherwise hand the user a file
      // with a `.pdf` extension that isn't actually a PDF.
      const contentType = (response.headers?.['content-type'] || response.data?.type || '').toLowerCase();
      if (response.status !== 200 || !contentType.includes('application/pdf')) {
        throw new Error(`Unexpected PDF response (status ${response.status}, type "${contentType || 'unknown'}")`);
      }

      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('P&L PDF export failed:', err);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // ── Resolve display state from data + UI ──
  // Use server-returned periods when available; otherwise mirror them locally
  // so the header and skeleton render correctly during initial load.
  const localPeriods = useMemo(() => buildPeriodsLocal(range, granularity), [range, granularity]);
  const periods = data?.periods || localPeriods;
  const showTotalCol = periods.length > 1;
  const showCompareCols = !isTrend && !!comparisonRange && !!data?.comparisonRange;
  const colCount = 1 + periods.length + (showTotalCol ? 1 : 0) + (showCompareCols ? 2 : 0);

  // ── Labels ──
  const activePresets = isTrend ? TREND_PRESETS : TOTAL_PRESETS;
  const rangeBtnLabel = useMemo(() => {
    if (rangePreset === 'custom') return fmtRangeLabel(range);
    const found = activePresets.find(p => p.key === rangePreset);
    return found ? found.label : fmtRangeLabel(range);
  }, [rangePreset, range, activePresets]);

  const compareBtnLabel = useMemo(() => {
    if (isTrend) return 'Compare unavailable';
    if (comparePreset === 'none') return 'Compare';
    if (comparePreset === 'custom' && comparisonRange) return fmtRangeLabel(comparisonRange);
    return COMPARE_PRESETS.find(c => c.key === comparePreset)?.label || 'Compare';
  }, [isTrend, comparePreset, comparisonRange]);

  const subtitle = useMemo(() => {
    if (!periods.length) return fmtRangeLabel(range);
    const left = fmtRangeLabel(range);
    if (showCompareCols) return `${left} vs ${fmtRangeLabel(comparisonRange)}`;
    return left;
  }, [periods, range, showCompareCols, comparisonRange]);

  // ── Render helpers ──
  const renderHeaderCells = () => (
    <tr>
      <th className="pl-th-name pl-sticky-col">ACCOUNT</th>
      {periods.map(p => (
        <th key={p.key} className="pl-th-amount">
          {isTrend ? p.label : fmtColumnRange(p)}
        </th>
      ))}
      {showTotalCol && <th className="pl-th-amount pl-total-col">TOTAL</th>}
      {showCompareCols && <th className="pl-th-amount">{fmtColumnRange(comparisonRange)}</th>}
      {showCompareCols && <th className="pl-th-amount">VARIANCE</th>}
    </tr>
  );

  const renderTotalRow = (key, total, opts = {}) => {
    if (!total) return null;
    const cls = `pl-row pl-computed ${opts.kind || ''}`;
    return (
      <tr key={key} className={cls}>
        <td className="pl-name-cell pl-sticky-col pl-computed-name">{total.label}</td>
        {periods.map((p, i) => (
          <td key={p.key} className="pl-amount-cell">{fmtCurrency(total.values[i])}</td>
        ))}
        {showTotalCol && <td className="pl-amount-cell pl-total-col">{fmtCurrency(total.total)}</td>}
        {showCompareCols && <td className="pl-amount-cell pl-cmp">{fmtCurrency(total.total_comparison)}</td>}
        {showCompareCols && (
          <td className={`pl-amount-cell pl-variance ${varianceClass(total.total_variance, total.favorable)}`}>
            {fmtCurrency(total.total_variance)}
          </td>
        )}
      </tr>
    );
  };

  const renderSection = (section) => {
    if (!section) return null;
    // Section paths are namespaced with `__section__/` so they don't collide
    // with account idPaths (which are `${section.key}/${accountId}/...`).
    const sectionPath = `__section__/${section.key}`;
    const isOpen = expanded[sectionPath] !== false; // default expanded
    return (
      <React.Fragment key={section.key}>
        <tr className={`pl-row pl-section-header ${isOpen ? '' : 'pl-section-collapsed'}`}>
          <td className="pl-name-cell pl-sticky-col">
            <div className="pl-section-header-content">
              <button
                className="pl-chevron pl-section-chevron"
                onClick={() => toggleNode(sectionPath)}
                aria-label={isOpen ? 'Collapse' : 'Expand'}
              >
                <svg className={`pl-chevron-icon ${isOpen ? 'open' : 'closed'}`} viewBox="0 0 10 10" aria-hidden="true">
                  <path d="M3 1.5 L7 5 L3 8.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <span className="pl-section-label">{section.label}</span>
            </div>
          </td>
          {periods.map((p, i) => (
            <td key={p.key} className="pl-amount-cell">{!isOpen ? fmtCurrency(section.values[i]) : ''}</td>
          ))}
          {showTotalCol && <td className="pl-amount-cell pl-total-col">{!isOpen ? fmtCurrency(section.total) : ''}</td>}
          {showCompareCols && <td className="pl-amount-cell pl-cmp">{!isOpen ? fmtCurrency(section.total_comparison) : ''}</td>}
          {showCompareCols && (
            <td className={`pl-amount-cell pl-variance ${!isOpen ? varianceClass(section.total_variance, section.favorable) : ''}`}>
              {!isOpen ? fmtCurrency(section.total_variance) : ''}
            </td>
          )}
        </tr>
        {isOpen && (
          <>
            {section.accounts.map(node => (
              <AccountRow
                key={node.id}
                node={node}
                depth={0}
                periods={periods}
                showTotal={showTotalCol}
                showCompare={showCompareCols}
                favorable={section.favorable}
                expanded={expanded}
                onToggle={toggleNode}
                idPath={`${section.key}/${node.id}`}
              />
            ))}
            <tr className="pl-row pl-section-total">
              <td className="pl-name-cell pl-sticky-col">{section.total_label}</td>
              {periods.map((p, i) => (
                <td key={p.key} className="pl-amount-cell">{fmtCurrency(section.values[i])}</td>
              ))}
              {showTotalCol && <td className="pl-amount-cell pl-total-col">{fmtCurrency(section.total)}</td>}
              {showCompareCols && <td className="pl-amount-cell pl-cmp">{fmtCurrency(section.total_comparison)}</td>}
              {showCompareCols && (
                <td className={`pl-amount-cell pl-variance ${varianceClass(section.total_variance, section.favorable)}`}>
                  {fmtCurrency(section.total_variance)}
                </td>
              )}
            </tr>
          </>
        )}
      </React.Fragment>
    );
  };

  const sectionByKey = (key) => data?.sections?.find(s => s.key === key);

  // Memoize section lookups so renderSection / conditional subtotals don't
  // traverse data.sections on every reference.
  const incomeSection       = sectionByKey('income');
  const cogsSection         = sectionByKey('cogs');
  const expenseSection      = sectionByKey('expense');
  const otherIncomeSection  = sectionByKey('other_income');
  const otherExpenseSection = sectionByKey('other_expense');

  return (
    <div className="wrapper pl-wrapper">
      <div className="pl-page">

        {/* ── Toolbar (filters live above the report) ── */}
        <div className="pl-toolbar">
          <div className="pl-toolbar-left">
            <div className="pl-toggle-strip">
              {VIEW_MODES.map(m => (
                <button
                  key={m.key}
                  className={`pl-toggle-btn ${viewMode === m.key ? 'active' : ''}`}
                  onClick={() => handleSetViewMode(m.key)}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          <div className="pl-toolbar-right">
            {/* Range selector */}
            <div className="pl-dropdown-anchor" ref={rangeRef}>
              <button
                className={`pl-control-btn ${rangePreset !== (isTrend ? 'last_12m' : 'ytd') ? 'has-filters' : ''}`}
                onClick={() => { setRangeOpen(p => !p); setCompareOpen(false); }}
              >
                {rangeBtnLabel}
                <span className="pl-control-caret" aria-hidden="true">▾</span>
              </button>
              {rangeOpen && (
                <div className="pl-dropdown">
                  <div className="pl-dropdown-heading">Range</div>
                  {activePresets.map(p => (
                    <button
                      key={p.key}
                      className={`pl-dropdown-option ${rangePreset === p.key ? 'selected' : ''}`}
                      onClick={() => handleSetRangePreset(p.key)}
                    >
                      {p.label}
                    </button>
                  ))}
                  <div className="pl-dropdown-sep" />
                  <div className="pl-dropdown-heading">Custom Range</div>
                  <div className="pl-date-row">
                    <input type="date" className="pl-date-input" value={customStart} onChange={e => setCustomStart(e.target.value)} />
                    <span className="pl-date-sep">→</span>
                    <input type="date" className="pl-date-input" value={customEnd} onChange={e => setCustomEnd(e.target.value)} />
                  </div>
                  <button
                    className="pl-dropdown-apply"
                    onClick={applyCustomRange}
                    disabled={!isCustomRangeValid}
                    title={isCustomRangeValid ? '' : 'Pick a start date that is before the end date'}
                  >
                    Apply
                  </button>
                </div>
              )}
            </div>

            {/* Compare selector (greyed out in trend modes) */}
            <div className="pl-dropdown-anchor" ref={compareRef}>
              <button
                className={`pl-control-btn ${comparePreset !== 'none' && !isTrend ? 'has-filters' : ''} ${isTrend ? 'pl-disabled' : ''}`}
                onClick={() => { if (!isTrend) { setCompareOpen(p => !p); setRangeOpen(false); } }}
                disabled={isTrend}
                title={isTrend ? 'Comparison is only available in Total view' : ''}
              >
                {compareBtnLabel}
                <span className="pl-control-caret" aria-hidden="true">▾</span>
              </button>
              {compareOpen && !isTrend && (
                <div className="pl-dropdown">
                  <div className="pl-dropdown-heading">Compare to</div>
                  {COMPARE_PRESETS.map(c => (
                    <button
                      key={c.key}
                      className={`pl-dropdown-option ${comparePreset === c.key ? 'selected' : ''}`}
                      onClick={() => { setComparePreset(c.key); setCompareOpen(false); }}
                    >
                      {c.label}
                    </button>
                  ))}
                  <div className="pl-dropdown-sep" />
                  <div className="pl-dropdown-heading">Custom Range</div>
                  <div className="pl-date-row">
                    <input type="date" className="pl-date-input" value={customCmpStart} onChange={e => setCustomCmpStart(e.target.value)} />
                    <span className="pl-date-sep">→</span>
                    <input type="date" className="pl-date-input" value={customCmpEnd} onChange={e => setCustomCmpEnd(e.target.value)} />
                  </div>
                  <button
                    className="pl-dropdown-apply"
                    onClick={applyCustomCompare}
                    disabled={!isCustomCompareValid}
                    title={isCustomCompareValid ? '' : 'Pick a start date that is before the end date'}
                  >
                    Apply
                  </button>
                </div>
              )}
            </div>

            <button
              className="pl-reset-btn"
              onClick={handleReset}
              title="Reset filters and expansion to defaults"
              aria-label="Reset filters and expansion to defaults"
            >
              <RefreshIcon className="pl-reset-icon" />
            </button>

            <div className="pl-dropdown-anchor" ref={downloadRef}>
              <button
                className="pl-download-btn"
                onClick={() => {
                  if (isExporting || loading || !data) return;
                  setDownloadOpen(p => !p);
                  setRangeOpen(false);
                  setCompareOpen(false);
                }}
                disabled={isExporting || loading || !data}
                title={data ? 'Download Profit & Loss' : 'No data to download yet'}
              >
                {isExporting ? (
                  <span className="pl-download-spinner" aria-hidden="true" />
                ) : (
                  <DownloadIcon className="pl-download-icon" />
                )}
              </button>
              {downloadOpen && (
                <div className="pl-dropdown pl-download-dropdown">
                  <button className="pl-dropdown-option" onClick={handleDownloadPdf}>
                    Download to PDF
                  </button>
                  <button className="pl-dropdown-option" onClick={handleDownloadExcel}>
                    Download to Excel
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Statement Report ── */}
        <div className="pl-report-section">
          <div className="pl-report">

            {/* Report header inside the document */}
            <div className="pl-report-head">
              <div className="pl-report-head-main">
                {workspace?.name && <div className="pl-report-eyebrow">{workspace.name}</div>}
                <h1 className="pl-report-title">Profit &amp; Loss</h1>
                <div className="pl-report-period">For the period {subtitle}</div>
              </div>
            </div>

            <div className="pl-report-rule" />

            <div className="pl-table-outer">
              <div className={`pl-table-wrapper ${isHorizScrolled ? 'is-scrolled' : ''}`} ref={tableWrapperRef}>
                <table className="pl-table">
                <thead>
                  {renderHeaderCells()}
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 14 }).map((_, i) => <SkeletonRow key={i} idx={i} columnCount={Math.max(2, colCount)} />)
                  ) : error ? (
                    <tr><td className="pl-empty-row pl-error-row" colSpan={colCount}>{error}</td></tr>
                  ) : !data || !data.sections || data.sections.length === 0 ? (
                    <tr><td className="pl-empty-row" colSpan={colCount}>No P&amp;L activity in this range.</td></tr>
                  ) : (
                    <>
                      {renderSection(incomeSection)}
                      {renderSection(cogsSection)}
                      {cogsSection && renderTotalRow('gross_profit', data.totals?.gross_profit, { kind: 'pl-subtotal' })}
                      {renderSection(expenseSection)}
                      {(expenseSection || cogsSection || incomeSection) &&
                        renderTotalRow('operating_income', data.totals?.operating_income, { kind: 'pl-subtotal' })}
                      {renderSection(otherIncomeSection)}
                      {renderSection(otherExpenseSection)}
                      {(otherIncomeSection || otherExpenseSection) &&
                        renderTotalRow('net_other_income', data.totals?.net_other_income, { kind: 'pl-subtotal' })}
                      {renderTotalRow('net_income', data.totals?.net_income, { kind: 'pl-grand-total' })}
                    </>
                  )}
                </tbody>
              </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProfitAndLoss;
